'use client'

import { Check, ChevronsRight, Loader2 } from 'lucide-react'
import {
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { cn } from '@/lib/utils'

/** How far right the handle must be let go for the deed to count. */
const COMMIT_AT = 0.9

/** The handle falling back to the left after a short drag. */
const RETURN_MS = 260

/** The handle finishing the last few per cent by itself after a good drag. */
const SETTLE_MS = 160

/** Breathing room between the pill's border and the handle, in px. */
const INSET = 4

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)'

/**
 * Measure before paint on the client, after mount on the server — the same swap
 * `RubberSegment` makes, for the same reason.
 *
 * `travel` cannot be known until the pill has been laid out, so the handle's
 * position is written from an effect. A passive `useEffect` runs AFTER the
 * browser has painted, which is one frame of the handle sitting at the left
 * edge — harmless at rest, wrong when the component mounts with `pending`
 * already true (a parent that remounts while the payment call is in flight) or
 * re-measures after a resize. `useLayoutEffect` closes that frame, and the
 * `typeof window` swap keeps it from warning on every server render.
 */
const useMeasure = typeof window === 'undefined' ? useEffect : useLayoutEffect

/**
 * „Przesuń, aby potwierdzić" — a handle that has to be carried to the far end
 * before anything happens.
 *
 * ── WHY A GESTURE AND NOT A BUTTON ───────────────────────────────────────
 * The owner asked for this at the step where an order turns into a payment.
 * That is the one screen in the panel where the cost of a mis-click is money
 * leaving somebody's account, and a pill that says „Zapłać" is a 40px target
 * sitting exactly where every other screen has taught the reader to click
 * without reading. A drag cannot be produced by a stray click, a trackpad
 * bounce or a double-submit, because it takes a second of deliberate travel —
 * which is the whole and only reason this component exists. It is NOT here to
 * look like an app.
 *
 * ── WHY role="button" AND NOT role="slider" ──────────────────────────────
 * A slider is how you announce a VALUE inside a range: a screen reader says
 * "0 of 100", arrow keys move by a step, and the reader is left to work out how
 * far is far enough and what happens when they get there. There is no value
 * here. There is one action and a threshold that exists purely to make the
 * gesture deliberate — so the element announces itself as the button it is, and
 * the threshold stays a property of the POINTER path, where it does its job.
 *
 * Which is why the keyboard does not imitate the drag. Right arrow, End, Enter
 * and Space each perform the deed outright. Making a keyboard user press an
 * arrow twenty times to "slide" would be a pantomime of safety: it protects
 * nobody, and any implementation of it would eventually close the payment
 * screen to everyone who does not hold a mouse. One `role="button"`, one
 * `aria-label`, four keys.
 *
 * ── ONE SET OF HANDLERS, NOT TWO ─────────────────────────────────────────
 * Pointer Events with `setPointerCapture`, so finger, mouse and pen run the
 * same code and the drag survives the cursor leaving the pill — no mouse/touch
 * pair to keep in sync, and no window-level listeners to leak.
 *
 * ── THE DRAG DOES NOT GO THROUGH REACT ───────────────────────────────────
 * `pointermove` fires per frame; routing it through `useState` would re-render
 * the payment screen's subtree a hundred times per gesture for two transforms.
 * The position lives in a ref and is written straight to the two elements that
 * show it. React state holds only what changes the WORDS on screen — which is
 * what has to be re-rendered and announced.
 *
 * The fill is revealed with `clip-path` rather than `scaleX`, because the brand
 * gradient has to stay the brand gradient: a scaled box squeezes the ramp into
 * the first few pixels and the colour reads wrong at every position but the
 * last.
 *
 * ── WHAT WAS DELIBERATELY LEFT OUT ───────────────────────────────────────
 * No auto-return timer — a half-dragged handle that creeps back while the
 * reader is looking at the price is a moving target, not a hint. No haptics,
 * no sound. No dragging BACK to cancel after the commit: by then the call has
 * gone out and pretending otherwise would be a lie in the interface.
 *
 * The commit latch does not reopen on its own. Once `onCommit` has fired this
 * instance is spent for its whole lifetime, which is what makes the
 * double-fire impossible rather than merely unlikely. A caller that wants a
 * second attempt after a failed payment remounts it with a new `key`.
 */
export function SlideCommit({
  label,
  committedLabel,
  onCommit,
  disabled = false,
  pending = false,
  className,
}: {
  /** What the pill says at rest — the action the drag will perform. */
  label: string
  /** What it says once the handle is home and the deed has gone out. */
  committedLabel: string
  /** The deed. Fired exactly once, ever. */
  onCommit: () => void
  /** Greys the pill out and refuses pointer and keyboard alike. */
  disabled?: boolean
  /** The deed is under way: handle parked at the end, all input ignored. */
  pending?: boolean
  className?: string
}) {
  const [committed, setCommitted] = useState(false)

  const rootRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLSpanElement>(null)

  /** Where the handle is, 0 at rest and 1 at the far end. */
  const progress = useRef(0)
  /** How many px of travel the handle has, measured rather than assumed. */
  const travel = useRef(0)
  /** The live drag: which pointer, where it went down, where the handle was. */
  const drag = useRef<{ id: number; x: number; from: number } | null>(null)
  /** The latch. A ref, so it is shut on the same tick the deed goes out. */
  const fired = useRef(false)
  const reduce = useRef(false)
  const glides = useRef<Animation[]>([])

  const locked = disabled || pending || committed

  /** Puts the handle and the fill where `at` says, with no animation at all. */
  const paint = useCallback((at: number) => {
    const thumb = thumbRef.current
    if (thumb) thumb.style.transform = `translateX(${at * travel.current}px)`
    const fill = fillRef.current
    if (fill) fill.style.clipPath = `inset(0 ${(1 - at) * 100}% 0 0)`
  }, [])

  /**
   * Moves the handle from `from` to `to` over `ms`.
   *
   * The end state is painted FIRST and the animation merely covers the journey,
   * so nothing runs with `fill: 'forwards'` and there is no animation state to
   * unwind if the reader grabs the handle mid-flight. It also makes
   * `prefers-reduced-motion` a one-line early return that lands on the correct
   * final position instead of an empty pill.
   */
  const glide = useCallback(
    (from: number, to: number, ms: number) => {
      for (const animation of glides.current) animation.cancel()
      glides.current = []

      progress.current = to
      paint(to)
      if (reduce.current || from === to) return

      const px = travel.current
      const easing = 'cubic-bezier(0.22, 1, 0.36, 1)'
      const thumb = thumbRef.current?.animate(
        [{ transform: `translateX(${from * px}px)` }, { transform: `translateX(${to * px}px)` }],
        { duration: ms, easing },
      )
      const fill = fillRef.current?.animate(
        [
          { clipPath: `inset(0 ${(1 - from) * 100}% 0 0)` },
          { clipPath: `inset(0 ${(1 - to) * 100}% 0 0)` },
        ],
        { duration: ms, easing },
      )
      glides.current = [thumb, fill].filter((animation): animation is Animation =>
        Boolean(animation),
      )
    },
    [paint],
  )

  const commit = useCallback(
    (from: number) => {
      glide(from, 1, SETTLE_MS)
      setCommitted(true)
      if (fired.current) return
      fired.current = true
      onCommit()
    },
    [glide, onCommit],
  )

  // The reader's motion preference, kept current: it can be changed in the OS
  // while this screen is open, and a payment screen is exactly where somebody
  // stops to turn animation off.
  useEffect(() => {
    const query = window.matchMedia(REDUCE_QUERY)
    reduce.current = query.matches
    const onChange = (event: MediaQueryListEvent) => {
      reduce.current = event.matches
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  // Travel is measured, not computed from the class names — the pill is
  // full-width and the panel's columns change on every breakpoint, so a
  // hardcoded number would put the threshold in a different place on a phone
  // than on a desk.
  useMeasure(() => {
    const track = trackRef.current
    const thumb = thumbRef.current
    if (!track || !thumb) return

    const measure = () => {
      travel.current = Math.max(0, track.clientWidth - thumb.offsetWidth - INSET * 2)
      paint(progress.current)
    }
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(track)
    return () => observer.disconnect()
  }, [paint])

  // A commit that came from somewhere else — the caller flipping `pending` on
  // its own, a form submitting — still has to leave the handle at the end,
  // otherwise the pill would say „Przetwarzam" with the handle sitting at zero.
  useMeasure(() => {
    if (!pending && !committed) return
    // …but a commit that started HERE has already sent the handle on its way,
    // and `glide` cancels every running animation before it looks at its
    // arguments. Without this line the re-render that `setCommitted` causes
    // would kill the settle the drag just started — the handle would snap the
    // last few per cent instead of finishing them, every single time.
    if (progress.current === 1) return
    glide(progress.current, 1, 0)
  }, [committed, glide, pending])

  // Nothing may be left running against an element that is going away: the
  // payment screen is replaced the moment the deed succeeds.
  useEffect(
    () => () => {
      for (const animation of glides.current) animation.cancel()
      glides.current = []
    },
    [],
  )

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (locked || travel.current === 0) return
    for (const animation of glides.current) animation.cancel()
    glides.current = []
    drag.current = { id: event.pointerId, x: event.clientX, from: progress.current }
    event.currentTarget.setPointerCapture(event.pointerId)
    // So that a reader who started with the mouse and let go halfway can finish
    // with Enter. `focus-visible` keeps the ring off for the pointer itself.
    rootRef.current?.focus()
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const live = drag.current
    if (!live || live.id !== event.pointerId) return
    // The caller can lock the pill with a finger still down — a form that
    // submitted on its own flips `pending`, the effect above parks the handle at
    // the end, and without this the very next move would drag it back out of
    // the position that says the deed is under way.
    if (locked) {
      drag.current = null
      return
    }
    const next = live.from + (event.clientX - live.x) / travel.current
    progress.current = Math.min(1, Math.max(0, next))
    paint(progress.current)
  }

  /** Letting go: past the threshold it finishes the trip, short of it it falls back. */
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const live = drag.current
    if (!live || live.id !== event.pointerId) return
    drag.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    const at = progress.current
    if (at >= COMMIT_AT) commit(at)
    else glide(at, 0, RETURN_MS)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (locked) return
    if (!['ArrowRight', 'End', 'Enter', ' ', 'Spacebar'].includes(event.key)) return
    // Space scrolls the page by default, and the payment summary is above this.
    event.preventDefault()
    // A held key repeats ~30 times a second. `fired` keeps the deed to one call,
    // but every repeat would still cancel and restart the settle — the handle
    // would sit frozen at the end for as long as the finger stayed down.
    if (event.repeat) return
    commit(progress.current)
  }

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: a real `<button>` fires on any click anywhere in it, which is the exact thing this component exists to prevent — the threshold would become decoration. So the role is declared and the keyboard is wired by hand. */}
      <div
        ref={rootRef}
        role="button"
        tabIndex={0}
        aria-label={committed || pending ? committedLabel : label}
        aria-disabled={locked || undefined}
        aria-busy={pending || undefined}
        onKeyDown={onKeyDown}
        className={cn(
          'relative isolate flex h-14 w-full min-w-56 items-center overflow-hidden rounded-full',
          'border border-[var(--panel-hairline-strong)] select-none',
          'focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:outline-none',
          locked && 'opacity-60',
          className,
        )}
        style={{ background: 'var(--panel-track)' }}
      >
        {/* The trail the handle leaves. It sits BEHIND the label rather than
            pushing it, so the words stay where the eye parked them while the
            colour arrives underneath — a label that slides is a label nobody can
            read mid-gesture. */}
        <span
          ref={fillRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10"
          style={{ background: 'var(--flou-gradient)', clipPath: 'inset(0 100% 0 0)' }}
        />

        {/* Measured against this element, not the border box: `travel` is read
            off it so the threshold means the same thing at every width. */}
        <div ref={trackRef} aria-hidden="true" className="absolute inset-0" />

        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 right-5 left-16 flex items-center justify-center',
            'truncate text-[0.82rem] font-semibold',
            committed || pending ? 'text-white' : 'text-white/70',
          )}
        >
          {committed || pending ? committedLabel : label}
        </span>

        {/* The handle. `touch-none` is what stops a phone from reading the drag
            as a page scroll and stealing the gesture halfway through. */}
        <div
          ref={thumbRef}
          aria-hidden="true"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className={cn(
            'absolute top-1 bottom-1 left-1 z-10 flex aspect-square touch-none items-center justify-center',
            'rounded-full text-white',
            locked ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
          )}
          style={{
            background: 'var(--panel-card-raised)',
            boxShadow: 'inset 0 0 0 1px color-mix(in oklab, oklch(1 0 0) 22%, transparent)',
          }}
        >
          {pending ? (
            <Loader2 className="size-5 animate-spin motion-reduce:animate-none" />
          ) : committed ? (
            <Check className="size-5" />
          ) : (
            <ChevronsRight className="size-5" />
          )}
        </div>
      </div>

      {/* The handle moves without a word being spoken, so the outcome is said
          out loud — a reader who committed with Enter never sees the pill
          change.

          OUTSIDE the pill, and that is not tidiness. `role="button"` is one of
          the roles ARIA marks "children presentational": everything inside it is
          stripped of its own semantics and flattened into the button's name, so
          a live region nested in there is not a weak announcement, it is no
          announcement at all. Moved out, it is a region in its own right again.

          `sr-only` is `position: absolute`, so this second root node is out of
          flow — it is not a flex or grid item in whatever the caller wraps this
          in, and the pill keeps the layout it had as a single element. */}
      <p role="status" aria-live="polite" className="sr-only">
        {pending || committed ? committedLabel : ''}
      </p>
    </>
  )
}
